import React, { useMemo, useRef } from 'react';
import {isAragUser, isSolicitorUser, TERMINAL_POLICY_STATUSES} from "../../helpers/functions";
import moment from "moment";
import { LEGAL_ADVICE_TEXT, POLICYHOLDER_TEXT } from '../../helpers/variables';
import { Button, FormInline, Link, Loader } from 'arag-ui-lib';
import {
    DO_NOT_DISPLAY_INCEPTION_DATE_POLICY_STATUSES, DOTS, LEFT,
    MEDIUM, MOMENT_DATE_FORMAT, NOTIFICATION_EVENT_STATUS_CHANGED, POLICY_STATUS_MN_ABANDONED,
    POLICY_STATUS_MN_CLOSED, POLICY_STATUS_MN_CLOSED_RA,
    POLICY_STATUS_MN_CLOSED_SC, POLICY_STATUS_MN_CLOSED_TF,
    POLICY_STATUS_MN_LOST, POLICY_STATUS_MN_WON_PART36, PRIMARY, RIGHT, TERTIARY
} from '../../helpers/constants';
import ModalWrapper from '../../helpers/modalWrapper';
import CONTENT_STRINGS from '../../helpers/contentStrings';
import { FORM_GROUP_VARIANTS } from '../../helpers/formGroupVariants';

export const ApplicationPolicyViewHeader = (props) => {
    const {
        UpdateTab,
        internalAlertsModalOpen,
        internalAlerts,
        toggleInternalAlertModal,
        User,
        SelectedApp,
        Store,
        toggleCostsSummaryModal,
        handleOpenStatusModal,
        handleMarkAsViewed,
        handleTransferCase,
    } = props;
    const internalAlertField = useRef(null);
    const alertsText = internalAlerts.map(e => e.internalAlertText).join(' ; ');
    const policyStatusLabel = SelectedApp.policyStatus;
    const policyStatusMachineName = SelectedApp.policyStatusMachineName;
    const selectedPolicyLiveDate = moment(SelectedApp.liveDate);
    const selectedPolicyInceptionDate = SelectedApp.inceptionDate ? moment(SelectedApp.inceptionDate) : null;
    const isTerminalStatus = TERMINAL_POLICY_STATUSES.includes(policyStatusMachineName);
    const selectedPolicyStatusTerminationDate = SelectedApp.terminationDate ? moment(SelectedApp.terminationDate) : null;
    const isAragUserLoggedIn = isAragUser(User.loggedInUser.roles);
    const isSolicitorLoggedIn = isSolicitorUser(User.loggedInUser.roles);
    const showMarkAsViewedButton = (isAragUserLoggedIn
        && (Store.notification != null ? Store.notification.id : 0) > 0
        && Store.notification.applicationId === SelectedApp.id
        && !SelectedApp.readByArag
    ) || (
        isSolicitorLoggedIn
        && ((Store.notification != null ? Store.notification.id : 0) > 0)
        && (Store.notification.eventName === NOTIFICATION_EVENT_STATUS_CHANGED)
    );

    const internalAlertIsOverflow = useMemo(() => {
        if (internalAlertField.current) {
            return internalAlertField.current?.scrollWidth > internalAlertField.current?.clientWidth;
        }
        return false;
    }, [alertsText.length, internalAlertField?.current]);

    const isTerminalPolicyDisplay = isAragUserLoggedIn
        && SelectedApp
        && (
            policyStatusMachineName === POLICY_STATUS_MN_ABANDONED
            || policyStatusMachineName === POLICY_STATUS_MN_CLOSED
            || policyStatusMachineName === POLICY_STATUS_MN_LOST
            || policyStatusMachineName === POLICY_STATUS_MN_WON_PART36
            || policyStatusMachineName === POLICY_STATUS_MN_CLOSED_RA
            || policyStatusMachineName === POLICY_STATUS_MN_CLOSED_TF
            || policyStatusMachineName === POLICY_STATUS_MN_CLOSED_SC
        );
    const inceptionDate = selectedPolicyInceptionDate ? selectedPolicyInceptionDate.format(MOMENT_DATE_FORMAT) : null;
    const terminationDate = selectedPolicyStatusTerminationDate ? selectedPolicyStatusTerminationDate.format(MOMENT_DATE_FORMAT) : null;
    const aragProvider = SelectedApp.aragProvider;
    const aragProviderStyle = aragProvider ? {backgroundColor: aragProvider.providerColour} : null;

    const leftColumnItems = [
        isAragUserLoggedIn ? {
            content: (
                <FormInline
                    variant={FORM_GROUP_VARIANTS.PLAIN}
                    label={`${CONTENT_STRINGS.CREATED_BY}: `}
                    inputProps={{
                        id: 'created_by_display',
                        label: SelectedApp.agentName,
                    }}
                    viewControlTestId={'policy-details-section-created-by'}
                />
            )
        } : null,
        isAragUserLoggedIn ? {
            content: (
                <FormInline
                    variant={FORM_GROUP_VARIANTS.PLAIN}
                    label={`${LEGAL_ADVICE_TEXT} ${CONTENT_STRINGS.FIRM}: `}
                    inputProps={{
                        id: 'lawsys_name_display',
                        label: SelectedApp.lawsysName,
                    }}
                    viewControlTestId={'policy-details-section-solicitor'}
                />
            )
        } : null,
        isAragUserLoggedIn ? {
            content: (
                <FormInline
                    variant={FORM_GROUP_VARIANTS.PLAIN}
                    label={`${LEGAL_ADVICE_TEXT} ${CONTENT_STRINGS.GROUP}: `}
                    inputProps={{
                        id: 'solicitor_group_display',
                        label: SelectedApp.solicitorGroupName,
                    }}
                    viewControlTestId={'policy-details-section-solicitor-group'}
                />
            )
        } : null,
        isAragUserLoggedIn ? {
            content: (
                <FormInline
                    variant={FORM_GROUP_VARIANTS.PLAIN}
                    label={`${LEGAL_ADVICE_TEXT} ${CONTENT_STRINGS.REFERENCE}: `}
                    inputProps={{
                        id: 'solicitor_reference_display',
                        label: SelectedApp.solicitorReference,
                    }}
                    viewControlTestId={'policy-details-section-solicitor-reference'}
                />
            )
        } : null,
        {
            content: (
                <FormInline
                    variant={FORM_GROUP_VARIANTS.PLAIN}
                    label={`${CONTENT_STRINGS.ARAG_REFERENCE}: `}
                    inputProps={{
                        id: 'arag_reference_display',
                        label: SelectedApp.aragReference,
                    }}
                    viewControlTestId={'policy-details-section-arag-reference'}
                />
            )
        },
        {
            content: (
                <FormInline
                    variant={FORM_GROUP_VARIANTS.PLAIN}
                    label={`${POLICYHOLDER_TEXT} ${CONTENT_STRINGS.NAME}: `}
                    inputProps={{
                        id: 'client_name_display',
                        label: SelectedApp.clientName,
                    }}
                    viewControlTestId={'policy-details-section-client-name'}
                />
            )
        },
        isAragUserLoggedIn && alertsText.length > 0
        ?
            {
                content: (
                    <>
                        {
                            internalAlerts.fetching
                            ?
                                <Loader variant={DOTS} dotSize={MEDIUM} />
                            :
                                <FormInline
                                    variant={FORM_GROUP_VARIANTS.PLAIN}
                                    label={`${CONTENT_STRINGS.INTERNAL_ALERTS}: `}
                                    inputProps={{
                                        id: 'internal_alerts_display',
                                        label: alertsText,
                                    }}
                                    viewControlTestId={'policy-details-section-internal-alerts'}
                                />
                        }
                        {
                            internalAlertIsOverflow
                            ?
                                <Link
                                    variant={TERTIARY}
                                    arrowDirection={LEFT}
                                    onClick={toggleInternalAlertModal}
                                    label={CONTENT_STRINGS.VIEW_MORE}
                                    linkTestId={'application-policy-internal-alerts'}
                                />
                            :
                                null
                        }
                    </>
                )
            }
        :
            null,
        (isTerminalPolicyDisplay && SelectedApp.lawsysCaseReference)
        ?
            {
                content: (
                    <FormInline
                        variant={FORM_GROUP_VARIANTS.PLAIN}
                        label={`${CONTENT_STRINGS.LAWSYS_CASE_REFERENCE}: `}
                        inputProps={{
                            id: 'lawsys_case_reference_display',
                            label: SelectedApp.lawsysCaseReference,
                        }}
                        viewControlTestId={'application-policy-lawsys-ref'}
                    />
                )
            }
        :
            null,
        (isTerminalPolicyDisplay && SelectedApp.lawsysCaseDate)
        ?
            {
                content: (
                    <FormInline
                        variant={FORM_GROUP_VARIANTS.PLAIN}
                        label={`${CONTENT_STRINGS.LAWSYS_CASE_DATE}: `}
                        inputProps={{
                            id: 'lawsys_case_date_display',
                            label: moment(SelectedApp.lawsysCaseDate).format(MOMENT_DATE_FORMAT),
                        }}
                        viewControlTestId={'application-policy-lawsys-date'}
                    />
                )
            }
        :
            null,
    ].filter(Boolean);

    const rightColumnItems = [
        {
            content: (
                <FormInline
                    variant={FORM_GROUP_VARIANTS.PLAIN}
                    label={`${CONTENT_STRINGS.STATUS}: `}
                    inputProps={{
                        id: 'policy_status_display',
                        label: policyStatusLabel,
                    }}
                    viewControlTestId={'policy-details-section-status'}
                />
            )
        },
        isAragUserLoggedIn && SelectedApp.isReactivated ? {
            content: (
                <FormInline
                    id="reactivation_date_fi"
                    variant={FORM_GROUP_VARIANTS.PLAIN}
                    label={`${CONTENT_STRINGS.REACTIVATION_DATE}: `}
                    inputProps={{
                        id: 'reactivation_date_display',
                        label: selectedPolicyLiveDate.format(MOMENT_DATE_FORMAT),
                    }}
                    viewControlTestId={'application-policy-reactivation-date'}
                />
            )
        } : null,
        !DO_NOT_DISPLAY_INCEPTION_DATE_POLICY_STATUSES.includes(policyStatusMachineName) ? {
            content: (
                <FormInline
                    variant={FORM_GROUP_VARIANTS.PLAIN}
                    label={`${CONTENT_STRINGS.INCEPTION_DATE}: `}
                    inputProps={{
                        id: 'inception_date_display',
                        label: inceptionDate,
                    }}
                    viewControlTestId={'policy-details-section-inception-date'}
                />
            )
        } : null,
        isTerminalStatus ? {
            content: (
                <FormInline
                    variant={FORM_GROUP_VARIANTS.PLAIN}
                    label={`${CONTENT_STRINGS.TERMINATION_DATE}: `}
                    inputProps={{
                        id: 'termination_date_display',
                        label: terminationDate,
                    }}
                    viewControlTestId={'policy-details-section-termination-date'}
                />
            )
        } : null,
        isAragUserLoggedIn ? {
            content: (
                <FormInline
                    variant={FORM_GROUP_VARIANTS.PLAIN}
                    label={`${CONTENT_STRINGS.ARAG_PROVIDER}: `}
                    inputProps={{
                        id: 'arag_provider',
                        label: aragProvider?.providerCode ?? null,
                        valueStyle: aragProviderStyle,
                    }}
                    viewControlTestId={'policy-details-section-arag-provider'}
                />
            )
        } : null,
        !isSolicitorLoggedIn
        ?
            {
                content: (
                    <div>
                        <div className={'noprintarea'}>
                            <Button
                                label={CONTENT_STRINGS.CHANGE_STATUS}
                                variant={PRIMARY}
                                arrowDirection={RIGHT}
                                onClick={handleOpenStatusModal}
                                buttonTestId={'policy-details-section-change-status'}
                            />
                        </div>
                    </div>
                )
            }
        :
            null,
    ].filter(Boolean);

    return (
        <>
            <div className={'col-sm-12'} >
                <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                    <div className={'row col-sm-8'} style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
                        {leftColumnItems.map((item, index) => (
                            <div key={index}>{item.content}</div>
                        ))}
                    </div>
                    <div className={'row col-sm-4'} style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
                        {rightColumnItems.map((item, index) => (
                            <div key={index}>{item.content}</div>
                        ))}
                    </div>
                </div>
            </div>
            <ModalWrapper
                isOpen={internalAlertsModalOpen}
                onClose={toggleInternalAlertModal}
                title={CONTENT_STRINGS.INTERNAL_ALERTS}
                modalTestId={'internal-alerts-modal'}
                buttonTestId={'internal-alerts-close'}
            >
                {internalAlerts.map((c, index) => (
                    <div key={index}>
                        <FormInline
                            label={c.internalAlertText}
                            variant={FORM_GROUP_VARIANTS.PLAIN}
                            inputProps={{
                                id: `internal_alert_${index}_display`,
                            }}
                            viewControlTestId={`internal-alerts-alert-${index}`}
                        />
                    </div>
                ))}
            </ModalWrapper>
            <div className="noprintarea">
                <div className="col-sm-12">
                    {
                        showMarkAsViewedButton
                        ?
                            <>
                                <Button
                                    label={CONTENT_STRINGS.MARK_AS_VIEWED}
                                    variant={PRIMARY}
                                    arrowDirection={RIGHT}
                                    onClick={handleMarkAsViewed}
                                    buttonTestId={'policy-details-section-mark-as-viewed'}
                                />
                                {Store.updatingNotification ? <Loader variant={DOTS} dotSize={MEDIUM} /> : null}
                            </>
                        :
                            null
                    }
                </div>
                {
                    isSolicitorLoggedIn
                    ?
                        <>
                            {
                                (UpdateTab.costsSummary !== '' && UpdateTab.fetchingCostsSummary === false)
                                ?
                                    <div className="col-sm-12">
                                        <Button
                                            label={CONTENT_STRINGS.COSTS_SUMMARY}
                                            variant={PRIMARY}
                                            arrowDirection={RIGHT}
                                            onClick={toggleCostsSummaryModal}
                                            buttonTestId={'application-policy-costs-summary'}
                                        />
                                    </div>
                                :
                                    null
                            }
                            {UpdateTab.fetchingCostsSummary ? <Loader variant={DOTS} dotSize={MEDIUM} /> : null}
                        </>
                    :
                        null
                }
            </div>
            <div className="noprintarea">
                <div className="col-sm-12">
                    {
                        isAragUserLoggedIn
                        ?
                            <Button
                                label={CONTENT_STRINGS.TRANSFER_CASE}
                                variant={PRIMARY}
                                arrowDirection={RIGHT}
                                onClick={handleTransferCase}
                                buttonTestId={'policy-details-section-transfer-case'}
                            />
                        :
                            null
                    }
                </div>
            </div>
        </>
    );
};

export default ApplicationPolicyViewHeader;